import { useState, useCallback, useRef, useEffect } from 'react';
import type { GraphData, DataGeneratorType, DataGeneratorConfig } from '@packages/shared';
import { generateGraphData } from '@packages/shared';

interface UseDataManagerOptions {
  defaultDataType?: DataGeneratorType;
  defaultNodeCount?: number;
  defaultEdgeCount?: number;
  onDataGenerated?: (data: GraphData) => void;
  externalData?: GraphData;
}

export function useDataManager(options: UseDataManagerOptions = {}) {
  const {
    defaultDataType = 'social',
    defaultNodeCount = 20,
    defaultEdgeCount = 30,
    onDataGenerated,
    externalData
  } = options;

  const [loading, setLoading] = useState(false);
  const [selectedDataType, setSelectedDataType] = useState<DataGeneratorType>(defaultDataType);
  const [nodeCount, setNodeCount] = useState(defaultNodeCount);
  const [edgeCount, setEdgeCount] = useState(defaultEdgeCount);
  const [currentData, setCurrentData] = useState<GraphData>(externalData || { nodes: [], edges: [] });
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 当externalData变化时更新currentData
  useEffect(() => {
    if (externalData) {
      setCurrentData(externalData);
    }
  }, [externalData]);

  // 防抖生成新数据
  const generateNewDataDebounced = useCallback(async () => {
    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      setLoading(false); // 清除之前的loading状态
    }

    setLoading(true);
    
    // 设置新的防抖定时器
    debounceTimerRef.current = setTimeout(async () => {
      try {
        // 获取当前最新的状态值
        const config: DataGeneratorConfig = {
          type: selectedDataType,
          nodeCount,
          edgeCount,
          seed: Date.now()
        };

        console.log('Debounced generation with config:', config);
        const newData = generateGraphData(config);
        console.log('Generated data:', newData);
        console.log('Nodes count:', newData.nodes.length);
        console.log('Edges count:', newData.edges.length);
        setCurrentData(newData);
        onDataGenerated?.(newData);
      } catch (error) {
        console.error('生成数据失败:', error);
      } finally {
        setLoading(false);
      }
    }, 150); // 减少防抖延迟到150ms
  }, [selectedDataType, nodeCount, edgeCount, onDataGenerated]);

  // 立即生成新数据（不防抖）
  const generateNewData = useCallback(async () => {
    // 清除防抖定时器避免冲突
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    setLoading(true);
    
    try {
      const config: DataGeneratorConfig = {
        type: selectedDataType,
        nodeCount,
        edgeCount,
        seed: Date.now()
      };

      console.log('Immediate generation with config:', config);
      const newData = generateGraphData(config);
      console.log('Generated data:', newData);
      console.log('Nodes count:', newData.nodes.length);
      console.log('Edges count:', newData.edges.length);
      setCurrentData(newData);
      onDataGenerated?.(newData);
    } catch (error) {
      console.error('生成数据失败:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDataType, nodeCount, edgeCount, onDataGenerated]);

  // 使用指定参数立即生成数据
  const generateDataWithParams = useCallback(async (nodes: number, edges: number, type?: DataGeneratorType) => {
    // 清除防抖定时器避免冲突
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    setLoading(true);
    
    try {
      const config: DataGeneratorConfig = {
        type: type || selectedDataType,
        nodeCount: nodes,
        edgeCount: edges,
        seed: Date.now()
      };

      console.log('Direct generation with params:', config);
      const newData = generateGraphData(config);
      console.log('Generated data:', newData);
      console.log('Nodes count:', newData.nodes.length);
      console.log('Edges count:', newData.edges.length);
      
      // 同时更新状态和数据
      setNodeCount(nodes);
      setEdgeCount(edges);
      if (type) setSelectedDataType(type);
      setCurrentData(newData);
      onDataGenerated?.(newData);
    } catch (error) {
      console.error('生成数据失败:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDataType, onDataGenerated]);

  // 更新数据类型
  const updateDataType = useCallback((type: DataGeneratorType) => {
    setSelectedDataType(type);
  }, []);

  // 更新节点数量
  const updateNodeCount = useCallback((count: number) => {
    setNodeCount(Math.max(5, Math.min(2000, count)));
  }, []);

  // 更新边数量
  const updateEdgeCount = useCallback((count: number) => {
    setEdgeCount(Math.max(5, Math.min(5000, count)));
  }, []);

  // 设置测试数据
  const setTestData = useCallback((nodes: number, edges: number) => {
    setNodeCount(nodes);
    setEdgeCount(edges);
  }, []);

  // 导出数据
  const exportData = useCallback(() => {
    const formattedData = JSON.stringify(currentData, null, 2);
    return formattedData;
  }, [currentData]);

  // 复制数据到剪贴板
  const copyDataToClipboard = useCallback(async () => {
    try {
      const dataStr = exportData();
      await navigator.clipboard.writeText(dataStr);
      return true;
    } catch (error) {
      console.error('复制失败:', error);
      return false;
    }
  }, [exportData]);

  // 下载数据文件
  const downloadData = useCallback(() => {
    const dataStr = exportData();
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `knowledge-graph-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [exportData]);

  return {
    loading,
    selectedDataType,
    nodeCount,
    edgeCount,
    currentData,
    generateNewData,
    generateNewDataDebounced,
    generateDataWithParams,
    updateDataType,
    updateNodeCount,
    updateEdgeCount,
    setTestData,
    exportData,
    copyDataToClipboard,
    downloadData
  };
}